import { useEffect, useState, useRef, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import LandingPage, { getStoredReferralCode, clearStoredReferralCode } from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import Settings from "./pages/Settings";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Unsubscribed from "./pages/Unsubscribed";
import BillingSuccess from "./pages/BillingSuccess";
import BillingCancel from "./pages/BillingCancel";
import AffiliatesPage from "./pages/AffiliatesPage";
import AffiliateDashboard from "./pages/AffiliateDashboard";
import AffiliateThankYou from "./pages/AffiliateThankYou";
import AffiliateVerify from "./pages/AffiliateVerify";
import AgencyPage from "./pages/AgencyPage";
import PortfolioDashboard from "./pages/PortfolioDashboard";
import AdminOverview from "./pages/AdminOverview";
import AdminAffiliates from "./pages/AdminAffiliates";
import AdminUsers from "./pages/AdminUsers";
import AdminCustomers from "./pages/AdminCustomers";
import AdminPayouts from "./pages/AdminPayouts";
import AdminWaitlist from "./pages/AdminWaitlist";
import AdminEarlyAccess from "./pages/AdminEarlyAccess";
import AdminTraffic from "./pages/AdminTraffic";
import Onboarding from "./pages/Onboarding";
import OnboardingPreview from "./pages/OnboardingPreview";
import DemoWeeklyInsight from "./pages/DemoWeeklyInsight";
import DemoEmailPreview from "./pages/DemoEmailPreview";
import DemoInsightCategories from "./pages/DemoInsightCategories";
import DemoOnboardingFlow from "./pages/DemoOnboardingFlow";
import EmbeddedApp from "./pages/EmbeddedApp";
import AuthHeader from "./components/AuthHeader";
import { ShopifyProvider, useShopify } from "./contexts/ShopifyContext";
import { API_BASE, isSameOrigin } from "./lib/apiBase";
import { Loader2 } from "lucide-react";
import { Button } from "./components/ui/button";
import { trackSignup } from "./utils/analytics";
import { trackPageView } from "./utils/traffic";

// Session token storage key (backup auth method for cross-origin edge cases)
const SESSION_TOKEN_KEY = 'replydriven_session_token';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

/**
 * Axios instance configured for API calls
 * 
 * IMPORTANT: 
 * - Uses relative URLs in production for cookie reliability
 * - Always includes credentials for cookie-based auth
 * - Falls back to Authorization header for cross-origin scenarios
 */
export const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,  // CRITICAL: Always include cookies
  headers: {
    'Content-Type': 'application/json',
  }
});

// Set default withCredentials for all axios requests
axios.defaults.withCredentials = true;

// Add interceptor to include session token in Authorization header (backup auth method)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(SESSION_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear stored token on auth failure
      localStorage.removeItem(SESSION_TOKEN_KEY);
    }
    return Promise.reject(error);
  }
);

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { setUser } = useAuth();

  useEffect(() => {
    // CRITICAL: Use useRef to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        toast.error("Authentication failed");
        navigate("/");
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        console.log("Processing auth callback with session_id...");
        
        // Get referral code if stored (from ?ref= URL parameter)
        const referralCode = getStoredReferralCode();
        if (referralCode) {
          console.log(`📎 Sending referral code with auth: ${referralCode}`);
        }
        
        const response = await api.post("/auth/session", { 
          session_id: sessionId,
          referral_code: referralCode  // Pass referral to backend
        });
        console.log("Session created successfully:", response.data?.email);
        
        // Store session token in localStorage for cross-origin auth
        if (response.data.session_token) {
          localStorage.setItem(SESSION_TOKEN_KEY, response.data.session_token);
          console.log("Session token stored in localStorage");
        }
        
        // Update auth context with user data (exclude token from user object)
        const { session_token, ...userData } = response.data;
        setUser(userData);
        
        // Track signup/login in GA4
        trackSignup('google');
        
        // BACKUP: Attach affiliate ref server-side (in case initial auth didn't capture it)
        // This ensures attribution survives even if cookies/localStorage are cleared later
        if (referralCode) {
          try {
            const attachRes = await api.post("/affiliates/attach-ref", { ref: referralCode });
            if (attachRes.data.success) {
              console.log(`✅ Affiliate attribution confirmed server-side: ${referralCode}`);
            } else {
              console.log(`ℹ️ Affiliate attach: ${attachRes.data.reason}`);
            }
          } catch (e) {
            console.warn("Could not attach affiliate ref:", e);
          }
          // Clear the stored referral code after attempting server-side attach
          clearStoredReferralCode();
        }
        
        // Check for returnTo in sessionStorage (set before OAuth redirect)
        const returnTo = sessionStorage.getItem('returnTo');
        sessionStorage.removeItem('returnTo'); // Clean up
        
        // Navigate to returnTo destination or dashboard
        const destination = returnTo ? decodeURIComponent(returnTo) : "/dashboard";
        navigate(destination, { replace: true });
        toast.success(`Welcome, ${response.data.name}!`);
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Authentication failed");
        navigate("/");
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#D4F34A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-text-muted">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4F34A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    // Redirect to login with returnTo parameter
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  return children;
};

// Login Page Component
const LoginPage = () => {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/dashboard';

  useEffect(() => {
    // If user is already authenticated, redirect to returnTo destination
    if (!loading && user) {
      navigate(decodeURIComponent(returnTo), { replace: true });
    }
  }, [user, loading, navigate, returnTo]);

  const handleLogin = () => {
    // Store returnTo in sessionStorage so it persists through OAuth redirect
    sessionStorage.setItem('returnTo', returnTo);
    login();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin" />
      </div>
    );
  }

  // If user is authenticated, show loading while redirect happens
  if (user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <div className="w-20 h-20 rounded-full bg-[#D4F34A]/10 flex items-center justify-center mx-auto mb-6">
          <svg viewBox="0 0 100 100" className="w-10 h-10">
            <path
              d="M20 80 L20 20 L55 20 C70 20 80 30 80 42 C80 52 72 60 60 62 L80 80 L60 80 L42 62 L40 62 L40 80 Z M40 35 L40 50 L52 50 C58 50 62 47 62 42.5 C62 38 58 35 52 35 Z"
              fill="#D4F34A"
            />
          </svg>
        </div>
        <h1 className="font-heading text-2xl font-bold text-white mb-2">
          Sign in to ReplyDriven
        </h1>
        <p className="text-text-muted mb-8">
          Get weekly profit insights for your Shopify store
        </p>
        <Button
          onClick={handleLogin}
          className="w-full bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold py-6"
        >
          Continue with Google
        </Button>
        <p className="text-xs text-text-muted mt-6">
          By signing in, you agree to our{" "}
          <a href="/terms" className="underline hover:text-white">Terms of Service</a>
          {" "}and{" "}
          <a href="/privacy" className="underline hover:text-white">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};

// Scroll to top on route change (except for anchor links)
const ScrollToTop = () => {
  const { pathname, hash, search } = useLocation();
  
  useEffect(() => {
    // Don't scroll if there's a hash (anchor link)
    if (hash) return;
    
    // Scroll to top on route change
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);

  // Fire lightweight page-view tracker (public pages only; util itself skips admin/embedded)
  useEffect(() => {
    trackPageView(pathname + (search || ""));
  }, [pathname, search]);
  
  return null;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();
  const { isEmbedded } = useShopify();

  // CRITICAL: Check for session_id SYNCHRONOUSLY during render
  // This prevents race conditions with ProtectedRoute
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // If embedded in Shopify, render embedded app experience
  // Detect via URL params or persisted state
  const params = new URLSearchParams(location.search);
  const hasHost = params.get('host');
  const isEmbeddedParam = params.get('embedded') === '1';
  
  if (isEmbedded || hasHost || isEmbeddedParam) {
    return <EmbeddedApp />;
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/app" element={<EmbeddedApp />} />
        <Route path="/app/*" element={<EmbeddedApp />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/preview"
        element={
          <ProtectedRoute>
            <OnboardingPreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/affiliates" element={<AffiliatesPage />} />
      <Route 
        path="/affiliates/dashboard" 
        element={
          <ProtectedRoute>
            <AffiliateDashboard />
          </ProtectedRoute>
        } 
      />
      <Route path="/affiliates/thank-you" element={<AffiliateThankYou />} />
      <Route path="/affiliates/verify" element={<AffiliateVerify />} />
      <Route path="/agency" element={<AgencyPage />} />
      <Route path="/partners/agencies" element={<AgencyPage />} />
      <Route
        path="/portfolio"
        element={
          <ProtectedRoute>
            <PortfolioDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Internal Demo Pages - Admin only, not linked publicly */}
      <Route path="/demo/weekly-insight" element={<DemoWeeklyInsight />} />
      <Route path="/demo/weekly-insight/:variation" element={<DemoWeeklyInsight />} />
      <Route path="/demo/email-preview" element={<DemoEmailPreview />} />
      <Route path="/demo/insight-categories" element={<DemoInsightCategories />} />
      <Route path="/demo/onboarding-flow" element={<DemoOnboardingFlow />} />
      
      <Route path="/admin" element={<AdminOverview />} />
      <Route path="/admin/affiliates" element={<AdminAffiliates />} />
      <Route path="/admin/users" element={<AdminUsers />} />
      <Route path="/admin/customers" element={<AdminCustomers />} />
      <Route path="/admin/payouts" element={<AdminPayouts />} />
      <Route path="/admin/waitlist" element={<AdminWaitlist />} />
      <Route path="/admin/early-access" element={<AdminEarlyAccess />} />
      <Route path="/admin/traffic" element={<AdminTraffic />} />
      <Route path="/unsubscribed" element={<Unsubscribed />} />
      <Route
        path="/billing/success"
        element={
          <ProtectedRoute>
            <BillingSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/cancel"
        element={
          <ProtectedRoute>
            <BillingCancel />
          </ProtectedRoute>
        }
      />
    </Routes>
    </>
  );
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // Check if we have a stored token
      const storedToken = localStorage.getItem(SESSION_TOKEN_KEY);
      if (!storedToken) {
        console.log("No stored session token found");
        setLoading(false);
        return;
      }
      
      try {
        console.log("Checking auth with stored token...");
        const response = await api.get("/auth/me");
        console.log("Auth check successful:", response.data?.email);
        setUser(response.data);
      } catch (error) {
        console.log("Auth check failed:", error?.response?.status);
        // Clear invalid token
        localStorage.removeItem(SESSION_TOKEN_KEY);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Always clear local storage and redirect
      localStorage.removeItem(SESSION_TOKEN_KEY);
      setUser(null);
      window.location.href = "/";
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ShopifyProvider>
        <AuthProvider>
          <div className="App dark">
            {/* Noise overlay for texture */}
            <div className="noise-overlay"></div>
            <AppRouter />
            <Toaster richColors position="top-right" />
          </div>
        </AuthProvider>
      </ShopifyProvider>
    </BrowserRouter>
  );
}

export default App;
